import { ReactNode, useState } from 'react';
import { type Option, SelectBox } from 'src/components/SelectBox';
import { CommunicationChannel } from 'src/services/campaigns/campaigns.interface';
import clsx from 'clsx';
import StepCardHeader, { StepMetrics } from './StepCardHeader';
import StepCardActions from './StepCardActions';
import StepCardSummary from './StepCardSummary';

type StepCardProps = {
  stepNumber: number;
  children: ReactNode;
  titleDefaultValue?: Option;
  titleOnChange?: (value: string) => void;
  metrics?: StepMetrics;
  summary?: ReactNode;
  defaultCollapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  className?: string;
  error?: boolean;
  onAddStepAbove?: () => void;
  onAddStepBelow?: () => void;
  onDeleteStep?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
};

export function StepCard({
  stepNumber,
  children,
  titleDefaultValue,
  titleOnChange,
  metrics,
  summary,
  defaultCollapsed = false,
  onToggleCollapse,
  className = '',
  error = false,
  onAddStepAbove,
  onAddStepBelow,
  onDeleteStep,
  icon,
  disabled = false,
}: StepCardProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const channelTitleOptions: Option[] = [
    {
      value: CommunicationChannel.CALL,
      label: 'Phone call',
    },
    {
      value: CommunicationChannel.EMAIL,
      label: 'Email',
    },
    {
      value: CommunicationChannel.SMS,
      label: 'SMS',
    },
  ];

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    onToggleCollapse?.(next);
  };

  return (
    <div
      className={clsx(
        'rounded-2xl border w-full min-w-[32rem]',
        error
          ? 'border-error'
          : 'border-neutral-200 hover:border-primary transition-colors duration-200',
        className,
      )}
    >
      <StepCardHeader
        stepNumber={stepNumber}
        title={
          <SelectBox
            options={channelTitleOptions}
            defaultValue={
              titleDefaultValue
                ? channelTitleOptions.find(
                    (o) => o.value === titleDefaultValue.value,
                  ) || titleDefaultValue
                : undefined
            }
            className="w-auto min-w-[10rem]"
            onChange={titleOnChange}
            disabled={stepNumber === 1 || disabled}
          />
        }
        icon={icon}
        metrics={metrics}
        actions={
          <StepCardActions
            stepNumber={stepNumber}
            onAddAbove={onAddStepAbove}
            onAddBelow={onAddStepBelow}
            onDelete={onDeleteStep}
            disabled={disabled}
          />
        }
        collapsed={collapsed}
        onToggle={toggle}
      />
      {!collapsed && (
        <div className="p-8 bg-neutral-50 rounded-b-2xl">{children}</div>
      )}
      {collapsed && <StepCardSummary>{summary}</StepCardSummary>}
    </div>
  );
}

export default StepCard;
